import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Tooltip,
  Button,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Drawer,
  Badge,
  Progress,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { QueryLog, QueryLogStatistics, queryLogsApi } from '../lib/queryLogsApi';
import { useAppSelector } from '../store/hooks';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface QueryLogsPageProps {}

const QueryLogsPage: React.FC<QueryLogsPageProps> = () => {
  const [logs, setLogs] = useState<QueryLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<QueryLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedClassification, setSelectedClassification] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedDataSource, setSelectedDataSource] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [statistics, setStatistics] = useState<QueryLogStatistics | null>(null);
  const [selectedLog, setSelectedLog] = useState<QueryLog | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [statsDrawerVisible, setStatsDrawerVisible] = useState(false);
  
  // Pagination
  const [currentPage, setCurrenPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Get current project from store
  const { currentProject } = useAppSelector((state) => state.project);

  useEffect(() => {
    if (currentProject) {
      fetchLogs();
      fetchStatistics();
    }
  }, [currentProject]);

  useEffect(() => {
    filterLogs();
  }, [logs, searchText, selectedClassification, selectedStatus, selectedDataSource, dateRange]);

  const fetchLogs = async () => {
    if (!currentProject) return;
    
    setLoading(true);
    try {
      const data = await queryLogsApi.getProjectLogs(currentProject.id, {
        limit: 1000, // Get more data for client-side filtering
      });
      setLogs(data);
    } catch (error) {
      console.error('Failed to fetch query logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    if (!currentProject) return;
    
    try {
      const data = await queryLogsApi.getStatistics({
        projectId: currentProject.id,
        startDate: dateRange[0]?.toISOString(),
        endDate: dateRange[1]?.toISOString(),
      });
      setStatistics(data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];

    // Text search
    if (searchText) {
      filtered = filtered.filter(log =>
        log.inputMessage.toLowerCase().includes(searchText.toLowerCase()) ||
        log.outputResponse?.toLowerCase().includes(searchText.toLowerCase()) ||
        log.generatedSQL?.toLowerCase().includes(searchText.toLowerCase()) ||
        log.user.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
        log.user.lastName.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Classification filter
    if (selectedClassification) {
      filtered = filtered.filter(log => log.classification === selectedClassification);
    }

    // Status filter
    if (selectedStatus) {
      filtered = filtered.filter(log => log.executionStatus === selectedStatus);
    }

    // DataSource filter
    if (selectedDataSource) {
      filtered = filtered.filter(log => log.dataSource?.id === selectedDataSource);
    }

    // Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(log => {
        const logDate = dayjs(log.createdAt);
        return logDate.isAfter(dateRange[0]!) && logDate.isBefore(dateRange[1]!);
      });
    }

    setFilteredLogs(filtered);
  };

  const handleRefresh = () => {
    fetchLogs();
    fetchStatistics();
  };

  const showLogDetails = (log: QueryLog) => {
    setSelectedLog(log);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'timeout': return 'warning';
      default: return 'default';
    }
  };

  const getClassificationColor = (classification?: string) => {
    switch (classification) {
      case 'EXECUTE_QUERY': return 'blue';
      case 'INFORMATION': return 'green';
      case 'ANALYSIS': return 'purple';
      default: return 'default';
    }
  };

  const columns: ColumnsType<QueryLog> = [
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      defaultSortOrder: 'descend',
      render: (createdAt) => (
        <Tooltip title={dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss')}>
          <Text>{dayjs(createdAt).format('MMM DD, HH:mm')}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      width: 150,
      render: (user) => (
        <Space direction="vertical" size={0}>
          <Text strong>{user.firstName} {user.lastName}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{user.email}</Text>
        </Space>
      ),
    },
    {
      title: 'Project',
      dataIndex: 'project',
      key: 'project',
      width: 120,
      render: (project) => (
        <Tooltip title={project.description}>
          <Text strong>{project.name}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Data Source',
      dataIndex: 'dataSource',
      key: 'dataSource',
      width: 130,
      render: (dataSource) => dataSource ? (
        <Space direction="vertical" size={0}>
          <Text>{dataSource.name}</Text>
          <Tag color="blue">{dataSource.connectorType}</Tag>
        </Space>
      ) : (
        <Text type="secondary">N/A</Text>
      ),
    },
    {
      title: 'Query',
      dataIndex: 'inputMessage',
      key: 'inputMessage',
      ellipsis: true,
      render: (inputMessage) => (
        <Tooltip title={inputMessage}>
          <Text>{inputMessage}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      width: 130,
      filters: [
        { text: 'Execute Query', value: 'EXECUTE_QUERY' },
        { text: 'Information', value: 'INFORMATION' },
        { text: 'Analysis', value: 'ANALYSIS' },
      ],
      onFilter: (value, record) => record.classification === value,
      render: (classification) => (
        <Tag color={getClassificationColor(classification)}>
          {classification || 'Unknown'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'executionStatus',
      key: 'executionStatus',
      width: 100,
      filters: [
        { text: 'Success', value: 'success' },
        { text: 'Error', value: 'error' },
        { text: 'Timeout', value: 'timeout' },
      ],
      onFilter: (value, record) => record.executionStatus === value,
      render: (status) => (
        <Badge
          status={status === 'success' ? 'success' : status === 'error' ? 'error' : 'warning'}
          text={status.charAt(0).toUpperCase() + status.slice(1)}
        />
      ),
    },
    {
      title: 'Vector DB',
      dataIndex: 'vectorDatabase',
      key: 'vectorDatabase',
      width: 100,
      render: (vectorDatabase, record) => vectorDatabase ? (
        <Space direction="vertical" size={0}>
          <Tag color="purple">{vectorDatabase.toUpperCase()}</Tag>
          {record.vectorResults !== undefined && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {record.vectorResults} results
            </Text>
          )}
        </Space>
      ) : (
        <Text type="secondary">N/A</Text>
      ),
    },
    {
      title: 'Processing Time',
      dataIndex: 'processingTime',
      key: 'processingTime',
      width: 130,
      sorter: (a, b) => (a.processingTime || 0) - (b.processingTime || 0),
      render: (processingTime) => (
        <Space>
          <ClockCircleOutlined />
          <Text>{processingTime ? `${processingTime}ms` : 'N/A'}</Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => showLogDetails(record)}
          size="small"
        />
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Space align="center" style={{ marginBottom: '16px' }}>
          <Title level={2} style={{ margin: 0 }}>Query Logs</Title>
          <Button
            icon={<BarChartOutlined />}
            onClick={() => setStatsDrawerVisible(true)}
          >
            Statistics
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>

        {/* Statistics Cards */}
        {statistics && (
          <Row gutter={16} style={{ marginBottom: '16px' }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Queries"
                  value={statistics.totalQueries}
                  prefix={<BarChartOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Success Rate"
                  value={statistics.successRate}
                  precision={1}
                  suffix="%"
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Avg Processing Time"
                  value={statistics.avgProcessingTime}
                  precision={0}
                  suffix="ms"
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Error Rate"
                  value={statistics.errorRate}
                  precision={1}
                  suffix="%"
                  prefix={<ExclamationCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={6}>
              <Input
                placeholder="Search logs..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={3}>
              <Select
                placeholder="Classification"
                value={selectedClassification}
                onChange={setSelectedClassification}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="EXECUTE_QUERY">Execute Query</Option>
                <Option value="INFORMATION">Information</Option>
                <Option value="ANALYSIS">Analysis</Option>
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="Status"
                value={selectedStatus}
                onChange={setSelectedStatus}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="success">Success</Option>
                <Option value="error">Error</Option>
                <Option value="timeout">Timeout</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Data Source"
                value={selectedDataSource}
                onChange={setSelectedDataSource}
                allowClear
                style={{ width: '100%' }}
              >
                {/* Get unique data sources from logs */}
                {Array.from(new Set(logs
                  .filter(log => log.dataSource)
                  .map(log => log.dataSource!.id)))
                  .map(dataSourceId => {
                    const dataSource = logs.find(log => log.dataSource?.id === dataSourceId)?.dataSource;
                    return dataSource ? (
                      <Option key={dataSource.id} value={dataSource.id}>
                        {dataSource.name} ({dataSource.connectorType})
                      </Option>
                    ) : null;
                  })}
              </Select>
            </Col>
            <Col span={8}>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </Card>
      </div>

      {/* Main Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredLogs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredLogs.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, size) => {
              setCurrenPage(page);
              setPageSize(size || 20);
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Query Log Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={5}>Basic Information</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>User: </Text>
                  <Text>{selectedLog.user.firstName} {selectedLog.user.lastName}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Timestamp: </Text>
                  <Text>{dayjs(selectedLog.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Project: </Text>
                  <Text>{selectedLog.project.name}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Data Source: </Text>
                  <Text>{selectedLog.dataSource ? `${selectedLog.dataSource.name} (${selectedLog.dataSource.connectorType})` : 'N/A'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Status: </Text>
                  <Badge
                    status={getStatusColor(selectedLog.executionStatus) as any}
                    text={selectedLog.executionStatus.charAt(0).toUpperCase() + selectedLog.executionStatus.slice(1)}
                  />
                </Col>
                <Col span={12}>
                  <Text strong>Processing Time: </Text>
                  <Text>{selectedLog.processingTime ? `${selectedLog.processingTime}ms` : 'N/A'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Classification: </Text>
                  <Tag color={getClassificationColor(selectedLog.classification)}>
                    {selectedLog.classification || 'Unknown'}
                  </Tag>
                </Col>
                <Col span={12}>
                  <Text strong>Session ID: </Text>
                  <Text>{selectedLog.sessionId || 'N/A'}</Text>
                </Col>
                {selectedLog.vectorDatabase && (
                  <>
                    <Col span={12}>
                      <Text strong>Vector Database: </Text>
                      <Tag color="purple">{selectedLog.vectorDatabase.toUpperCase()}</Tag>
                    </Col>
                    <Col span={12}>
                      <Text strong>Vector Results: </Text>
                      <Text>{selectedLog.vectorResults || 'N/A'}</Text>
                    </Col>
                    {selectedLog.vectorScore && (
                      <Col span={12}>
                        <Text strong>Vector Score: </Text>
                        <Text>{selectedLog.vectorScore.toFixed(4)}</Text>
                      </Col>
                    )}
                    {selectedLog.vectorCollection && (
                      <Col span={12}>
                        <Text strong>Vector Collection: </Text>
                        <Text>{selectedLog.vectorCollection}</Text>
                      </Col>
                    )}
                  </>
                )}
              </Row>
            </div>

            <div>
              <Title level={5}>Input Message</Title>
              <Card size="small">
                <Paragraph copyable>{selectedLog.inputMessage}</Paragraph>
              </Card>
            </div>

            {selectedLog.generatedSQL && (
              <div>
                <Title level={5}>Generated SQL</Title>
                <Card size="small">
                  <Paragraph copyable>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {selectedLog.generatedSQL}
                    </pre>
                  </Paragraph>
                </Card>
              </div>
            )}

            {selectedLog.outputResponse && (
              <div>
                <Title level={5}>Output Response</Title>
                <Card size="small">
                  <Paragraph copyable>{selectedLog.outputResponse}</Paragraph>
                </Card>
              </div>
            )}

            {selectedLog.vectorQuery && (
              <div>
                <Title level={5}>Vector Database Query</Title>
                <Card size="small">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>Query: </Text>
                      <Paragraph copyable>{selectedLog.vectorQuery}</Paragraph>
                    </div>
                    {selectedLog.vectorMetadata && (
                      <div>
                        <Text strong>Metadata: </Text>
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                          {JSON.stringify(selectedLog.vectorMetadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </Space>
                </Card>
              </div>
            )}

            {selectedLog.errorMessage && (
              <div>
                <Title level={5}>Error Message</Title>
                <Card size="small">
                  <Text type="danger">{selectedLog.errorMessage}</Text>
                </Card>
              </div>
            )}
          </Space>
        )}
      </Modal>

      {/* Statistics Drawer */}
      <Drawer
        title="Query Statistics"
        placement="right"
        onClose={() => setStatsDrawerVisible(false)}
        open={statsDrawerVisible}
        width={400}
      >
        {statistics && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="Overall Statistics">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Statistic title="Total Queries" value={statistics.totalQueries} />
                <Statistic title="Average Processing Time" value={statistics.avgProcessingTime} suffix="ms" />
                <div>
                  <Text strong>Success Rate: </Text>
                  <Progress percent={statistics.successRate} status="success" />
                </div>
                <div>
                  <Text strong>Error Rate: </Text>
                  <Progress percent={statistics.errorRate} status="exception" />
                </div>
              </Space>
            </Card>

            <Card title="Classification Breakdown">
              <Space direction="vertical" style={{ width: '100%' }}>
                {statistics.classificationStats.map((stat) => (
                  <div key={stat.classification}>
                    <Text strong>{stat.classification}: </Text>
                    <Text>{stat.count}</Text>
                  </div>
                ))}
              </Space>
            </Card>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default QueryLogsPage;